import { useState, useEffect, useContext, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ical from "ical.js";
import {
  getPageRoute,
  useTrackCriticalAnalyticsEvent,
  useGetCalendarsByHandle,
  useGetCalendarEvents,
} from "src/services";
import { AuthContext } from "../utils/AuthContext.js";
import { getCriticalAnalyticsIdentifiers } from "./gtag.js";
import { getPastEvents, selectUpcomingEvents } from "../utils/Utils.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import { usePrevious } from "./usePrevious.js";

/** Stable empty list, so gating events out does not churn consumers' memos. */
const NO_EVENTS = [];

export const useTimelineContent = ({
  handle,
  calendarId,
  refetch = true,
  isEmbedded = false,
  notFetchEvents = false,
  trackCriticalTimelineView = false,
}) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const lastTrackedCalendarIdRef = useRef(null);
  const lastProcessedEventsRef = useRef(null);
  const [calendars, setCalendars] = useState([]);
  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingTotal, setUpcomingTotal] = useState(0);
  const [eventsCalendarId, setEventsCalendarId] = useState(null);
  const [pastEvents, setPastEvents] = useState([]);
  const [calendarCreator, setCalendarCreator] = useState();
  const [selectedCalendar, setSelectedCalendar] = useState(false);
  const prevSelectedCalendar = usePrevious(selectedCalendar);
  const [initialLoad, setInitialLoad] = useState(true);
  // Which calendar the last read settled for, however it settled. Distinct from
  // `eventsCalendarId`, which only names a read that produced usable events —
  // the page needs "still loading" and "loaded nothing" to look different.
  const [settledCalendarId, setSettledCalendarId] = useState(undefined);
  const [parseErrorCalendarId, setParseErrorCalendarId] = useState(null);
  const [calendarNotFound, setCalendarNotFound] = useState(false);
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();

  const creatorView = calendarCreator?._id === user?._id;
  const { mutateAsync: trackCriticalAnalyticsEvent } =
    useTrackCriticalAnalyticsEvent();

  useEffect(() => {
    // Only navigate when the calendar _id actually changes, not when
    // selectedCalendar gets a new object reference from a background refetch.
    // A spurious navigate strips in-flight query params (e.g. ?subscribing=).
    // Embedded timelines must remain on their embed URL as their data refreshes.
    if (
      !isEmbedded &&
      selectedCalendar?._id &&
      prevSelectedCalendar &&
      selectedCalendar._id !== prevSelectedCalendar?._id
    ) {
      navigate(
        getPageRoute("calendar", "CALENDAR_EVENTS", {
          handle,
          calendarId: selectedCalendar?._id,
        }),
      );
    }
  }, [isEmbedded, selectedCalendar]);

  const navigateToHandle = () => {
    if (location.pathname === `/${handle}`) return;
    navigate(getPageRoute("calendar", "ALL_EVENTS", { handle }));
    window.location.reload();
  };

  const onSuccessGetCalendars = (data) => {
    const items = [
      ...(data.creator?.createdCalendars || []),
      ...(data.creator?.createdGroups || []),
    ];
    setCalendars(items);
    if (data && data.creator && items.length > 0) {
      const selected = calendarId
        ? items.find((c) => c._id === calendarId)
        : items[0]; // set selected calendar as either the one associated with the :id in the URL or the first calendar (if url is just /handle)
      if (!selected && !isEmbedded) {
        navigateToHandle();
        setCalendarNotFound(true);
      } else if (!selected && isEmbedded) {
        setCalendarNotFound(true);
      } else {
        setSelectedData(data, selected);
      }
    } else {
      navigateToHandle();
      setCalendarNotFound(true);
    }
  };

  const setSelectedData = (data, selected) => {
    setSelectedCalendar(selected);
    setCalendarCreator(data.creator);
    setCalendarNotFound(false);
  };

  const onSuccessGetCalendarEvents = (data) => {
    // Parse webcal contents
    if (data) {
      try {
        const jcalData = ical.parse(data);
        const comp = new ical.Component(jcalData);

        const vevents = comp.getAllSubcomponents("vevent");
        const allUpcoming = selectUpcomingEvents(vevents);
        const upcoming = allUpcoming.slice(0, 100);
        const past = getPastEvents(vevents, 25);
        // Update the state with the parsed events
        setEvents(vevents);
        // Splitting and sort events into upcoming and past using helper functions (in Utils.js)
        setUpcomingEvents(upcoming);
        setUpcomingTotal(allUpcoming.length);
        setPastEvents(past);
        setEventsCalendarId(selectedCalendar._id);
        setSettledCalendarId(selectedCalendar._id);
        setParseErrorCalendarId(null);
        return true;
      } catch (error) {
        console.error(error);
        // ex: /disneyplus-soundtrack-1/625001efbb55c8e69805591d
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          "An error occurred loading the calendar.",
          "error"
        );
        setSettledCalendarId(selectedCalendar?._id ?? null);
        setParseErrorCalendarId(selectedCalendar?._id ?? null);
        return false;
      }
    }
    setSettledCalendarId(selectedCalendar?._id ?? null);
    setParseErrorCalendarId(selectedCalendar?._id ?? null);
    return false;
  };

  const trackTimelineCalendarView = async (calendar) => {
    if (!trackCriticalTimelineView || !user?._id || !calendar?._id) return;

    const calendarIdToTrack = String(calendar._id);
    if (lastTrackedCalendarIdRef.current === calendarIdToTrack) return;

    lastTrackedCalendarIdRef.current = calendarIdToTrack;

    try {
      const { clientId, sessionId } = await getCriticalAnalyticsIdentifiers();

      await trackCriticalAnalyticsEvent({
        eventName: "timeline_calendar_viewed",
        clientId,
        sessionId,
        customParams: {
          calendarId: calendarIdToTrack,
        },
      });
    } catch (error) {
      // Fail quiet on analytics transport issues, but allow a future retry.
      if (lastTrackedCalendarIdRef.current === calendarIdToTrack) {
        lastTrackedCalendarIdRef.current = null;
      }
      console.warn("Failed to track timeline calendar view", error);
    }
  };

  const {
    refetch: refetchCalendars,
    isLoading,
    data: dataCalendars,
    isError,
  } = useGetCalendarsByHandle({ handle }, {});

  const link = useMemo(
    () => selectedCalendar?.stanzaLink?.replace("webcal://", "http://") || "",
    [selectedCalendar?.stanzaLink]
  );

  const {
    refetch: refetchCalendarEvents,
    data: dataCalendarEvents,
    isLoading: isLoadingCalendarEvents,
    isError: isQueryErrorCalendarEvents,
    isSuccess: isSuccessCalendarEvents,
    isFetching: isFetchingCalendarEvents,
  } = useGetCalendarEvents(
    {
      link,
      updated: selectedCalendar?.updatedAt,
    },
    {
      enabled: !!selectedCalendar?.stanzaLink,
      isOwnProfile: creatorView,
    }
  );

  useEffect(() => {
    if (dataCalendars) {
      onSuccessGetCalendars(dataCalendars);
    }
    setInitialLoad(false);
  }, [dataCalendars]);

  useEffect(() => {
    if (dataCalendars && !notFetchEvents) {
      // Only reprocess when events data changes, not when selectedCalendar
      // gets a new reference from a background refetch. A spurious re-run
      // triggers the navigate effect (selectedCalendar dep) and strips
      // in-flight query params like ?subscribing before the flow completes.
      // Keyed by calendar too, because two calendars can return byte-identical
      // feeds and the payload alone would leave the second without events. Only
      // once the query has settled for this calendar, though: mid-switch the
      // previous calendar's payload is still on hand, and keying by calendar
      // alone would re-stamp it as this one's.
      const settled = isSuccessCalendarEvents && !isFetchingCalendarEvents;
      const read = { calendarId: selectedCalendar?._id, payload: dataCalendarEvents };
      const last = lastProcessedEventsRef.current;
      if (settled) {
        if (read.calendarId !== last?.calendarId || read.payload !== last?.payload) {
          lastProcessedEventsRef.current = read;
          const parsedSuccessfully = onSuccessGetCalendarEvents(dataCalendarEvents);
          if (parsedSuccessfully) {
            void trackTimelineCalendarView(selectedCalendar);
          }
        } else if (
          (eventsCalendarId === read.calendarId || parseErrorCalendarId === read.calendarId) &&
          settledCalendarId !== read.calendarId
        ) {
          // A cached read can be reused after another calendar's failed read.
          // The payload is unchanged, but the selected calendar still settled.
          setSettledCalendarId(read.calendarId);
        }
      }
    }
    if (isQueryErrorCalendarEvents) {
      // Nothing to clear: `eventsCalendarId` still names the calendar these
      // events came from, so the gate below withholds them.
      setSettledCalendarId(selectedCalendar?._id ?? null);
    }
  }, [
    dataCalendarEvents,
    dataCalendars,
    eventsCalendarId,
    isQueryErrorCalendarEvents,
    isFetchingCalendarEvents,
    isSuccessCalendarEvents,
    notFetchEvents,
    parseErrorCalendarId,
    selectedCalendar,
    settledCalendarId,
    trackCriticalAnalyticsEvent,
    trackCriticalTimelineView,
    user?._id,
  ]);

  // on page load
  useEffect(() => {
    if (refetch) {
      refetchCalendars();
    }
  }, []);

  useEffect(() => {
    if (refetch) {
      if (selectedCalendar?.stanzaLink) refetchCalendarEvents();
    }
  }, [selectedCalendar]);

  // Events outlive the calendar they were read for: a switch keeps the previous
  // calendar's on hand until the new read lands, and a failed read keeps them
  // indefinitely. Nothing downstream should ever see another calendar's events.
  const hasParseError = Boolean(
    selectedCalendar?._id && parseErrorCalendarId === selectedCalendar._id,
  );
  const isErrorCalendarEvents = isQueryErrorCalendarEvents || hasParseError;
  const ownEvents = eventsCalendarId === selectedCalendar?._id && !isErrorCalendarEvents;
  const isLoadingEvents = settledCalendarId !== (selectedCalendar?._id ?? null);

  const refreshEventBuckets = useCallback(() => {
    if (eventsCalendarId !== selectedCalendar?._id || isErrorCalendarEvents) return;
    const allUpcoming = selectUpcomingEvents(events);
    setUpcomingEvents(allUpcoming.slice(0, 100));
    setUpcomingTotal(allUpcoming.length);
    setPastEvents(getPastEvents(events, 25));
  }, [events, eventsCalendarId, isErrorCalendarEvents, selectedCalendar?._id]);

  const retryCalendarEvents = () => {
    // A retry must parse a new successful read even when its bytes match the
    // previous, unparseable payload.
    lastProcessedEventsRef.current = null;
    return refetchCalendarEvents();
  };

  return {
    dataCalendarEvents,
    calendars,
    events: ownEvents ? events : NO_EVENTS,
    upcomingEvents: ownEvents ? upcomingEvents : NO_EVENTS,
    upcomingTotal: ownEvents ? upcomingTotal : 0,
    eventsCalendarId,
    pastEvents: ownEvents ? pastEvents : NO_EVENTS,
    calendarCreator,
    description: selectedCalendar?.description,
    tags: selectedCalendar?.tags,
    isLoadingEvents,
    initialLoad,
    creatorView,
    selectedCalendar,
    setSelectedCalendar,
    refetchCalendars,
    refetchCalendarEvents: retryCalendarEvents,
    refreshEventBuckets,
    navigateToHandle,
    isLoading,
    invalidCreator: isError,
    isLoadingCalendarEvents,
    isErrorCalendarEvents,
    calendarNotFound,
  };
};
